import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { X, Search } from "lucide-react";
import FormField from "../ui/FormField";
import { Button } from "../ui/Button";
import { getSunbedCustomerPackages, SunbedCustomerPackageApi, SunbedRoomApi } from "../../services/sunbeds";

interface CreateSessionDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
}

interface CustomerWithPackage {
    id: number;
    customerId: number;
    customerName: string;
    phone: string;
    packageName: string;
    remainingMinutes: number;
    displayLabel: string;
    availableRooms: SunbedRoomApi[];
    packageId: number;
}

export const CreateSessionDrawer = ({
    isOpen,
    onClose,
    onSave,
}: CreateSessionDrawerProps) => {
    const [customer, setCustomer] = useState("");
    const [room, setRoom] = useState("");
    const [duration, setDuration] = useState("");
    const [delay, setDelay] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPackage, setSelectedPackage] = useState<CustomerWithPackage | null>(null);
    const [customersWithPackages, setCustomersWithPackages] = useState<CustomerWithPackage[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    // Fetch customers with packages
    const fetchCustomersWithPackages = useCallback(async () => {
        setLoading(true);
        let isMounted = true;
        try {
            const packagesRes = await getSunbedCustomerPackages();
            if (!isMounted) return;
            const packages: SunbedCustomerPackageApi[] = packagesRes.data || [];

            const merged: CustomerWithPackage[] = packages
                .map((pkg) => {
                    const customerDetail = pkg.user;
                    const phone = customerDetail?.mobile || customerDetail?.formatted_mobile || "";
                    const customerName = customerDetail?.name || "Unknown";
                    const packageName = pkg.package_name || pkg.sunbed?.label || "Sunbed Package";

                    return {
                        id: pkg.id,
                        customerId: pkg.user_id,
                        customerName,
                        phone,
                        packageName,
                        remainingMinutes: pkg.remaining_balance, // Updated to use correct field
                        displayLabel: `${customerName}${phone ? ` (${phone})` : ""} - ${packageName} (${pkg.remaining_balance} mins)`,
                        availableRooms: pkg.sunbed?.rooms?.filter(r => r.availability === 'available') || [],
                        packageId: pkg.id // Use pkg.id as packageId
                    };
                })
                .filter((item) => item.customerName !== "Unknown");

            setCustomersWithPackages(merged);
        } catch (error) {
            if (isMounted) console.error("Failed to fetch customers with packages:", error);
        } finally {
            if (isMounted) setLoading(false);
        }
        return () => {
            isMounted = false;
        };
    }, []);

    // Effect for fetching customers when the drawer is open
    useEffect(() => {
        if (isOpen) {
            fetchCustomersWithPackages();
            // Reset form when drawer opens
            setCustomer("");
            setSelectedPackage(null);
            setRoom("");
            setDuration("");
            setDelay("");
            setSearchTerm("");
            setShowDropdown(false);
        }
    }, [isOpen, fetchCustomersWithPackages]);

    // Memoized filtered customers
    const filteredCustomers = useMemo(() => {
        if (!searchTerm.trim()) {
            return customersWithPackages;
        }

        const term = searchTerm.toLowerCase();
        return customersWithPackages.filter(
            (item) =>
                item.customerName.toLowerCase().includes(term) ||
                item.phone.toLowerCase().includes(term) ||
                item.packageName.toLowerCase().includes(term)
        );
    }, [searchTerm, customersWithPackages]);

    const handleCustomerSelect = (selected: CustomerWithPackage) => {
        setCustomer(selected.displayLabel);
        setSelectedPackage(selected);
        setSearchTerm(""); // Clear search term as input is replaced
        setShowDropdown(false);
        setRoom(""); // Reset room when customer changes
    };

    const clearSelection = () => {
        setCustomer("");
        setSelectedPackage(null);
        setSearchTerm("");
        setRoom("");
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const selectedCustomer = customersWithPackages.find(
            (c) => c.displayLabel === customer
        );
        if (!selectedCustomer) {
            toast.error("Please select a valid customer");
            return;
        }

        // Find selected room object to get tmax_id
        // room state holds the room ID as string
        const selectedRoom = selectedCustomer.availableRooms.find(
            r => r.id.toString() === room
        );

        onSave({
            customer: selectedCustomer.displayLabel,
            customerId: selectedCustomer.customerId,
            room, // This will be the ID string now
            tmaxId: selectedRoom?.tmax_id, // Pass tmaxId if available
            duration,
            delay,
            packageId: selectedCustomer.packageId,
            remainingBalance: selectedCustomer.remainingMinutes,
        });
        onClose();
    };

    // Effect for closing dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.customer-search-container')) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showDropdown]);

    return (
        <>
            {/* Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
                    onClick={onClose}
                />
            )}

            {/* Drawer Panel */}
            <div
                className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"
                    }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button (Floating like BookingDetailsDrawer) */}
                {isOpen && (
                    <button
                        aria-label="Close drawer"
                        className="fixed -left-14 top-9 w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center text-[#885ABB] hover:bg-purple-50 transition z-[60]"
                        onClick={onClose}
                    >
                        <X strokeWidth={3} />
                    </button>
                )}

                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="px-6 py-6 border-b border-gray-100 bg-white">
                        <h2 className="text-2xl font-semibold text-[#402B69]">
                            Create Session
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Select a customer and configure sunbed session
                        </p>
                    </div>

                    {/* Content */}
                    <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* Customer Search Field */}
                        <div className="space-y-2 customer-search-container">
                            <label className="text-sm font-semibold text-[#402B69]">
                                Select Customer *
                            </label>
                            <div className="relative">
                                {!customer ? (
                                    <>
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                            <input
                                                type="text"
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    setShowDropdown(true);
                                                }}
                                                onFocus={() => setShowDropdown(true)}
                                                placeholder="Search by name, phone..."
                                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent"
                                            />
                                        </div>

                                        {/* Dropdown */}
                                        {showDropdown && (
                                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {loading ? (
                                                    <div className="p-4 text-center text-gray-500 text-sm">
                                                        Loading customers...
                                                    </div>
                                                ) : filteredCustomers.length === 0 ? (
                                                    <div className="p-4 text-center text-gray-500 text-sm">
                                                        No customers found
                                                    </div>
                                                ) : (
                                                    filteredCustomers.map((item) => (
                                                        <button
                                                            key={item.id}
                                                            type="button"
                                                            onClick={() => handleCustomerSelect(item)}
                                                            className="w-full text-left px-4 py-3 hover:bg-[#F3E8FF] transition-colors border-b border-gray-100 last:border-b-0"
                                                        >
                                                            <div className="font-medium text-gray-800">
                                                                {item.customerName}
                                                            </div>
                                                            <div className="text-xs text-gray-500 mt-0.5">
                                                                {item.packageName} • {item.remainingMinutes} mins left
                                                            </div>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="p-3 bg-[#F3E8FF] rounded-lg border border-[#C4A6E1] text-sm text-[#885ABB] flex justify-between items-center">
                                        <div className="flex flex-col">
                                            <span className="font-semibold">{customer.split("-")[0].trim()}</span>
                                            <span className="text-xs opacity-75">{selectedPackage?.packageName} ({selectedPackage?.remainingMinutes} mins)</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={clearSelection}
                                            className="p-1 hover:bg-[#E0C8F6] rounded-full transition-colors text-[#885ABB] hover:text-[#5e3a85] focus:outline-none"
                                            title="Clear selection"
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <FormField
                            label="Select Room *"
                            name="room"
                            type="select"
                            value={room}
                            onChange={(_, val) => setRoom(val)}
                            options={selectedPackage?.availableRooms.map(r => ({ value: r.id.toString(), label: r.label })) || []}
                            placeholder={selectedPackage ? (selectedPackage.availableRooms.length > 0 ? "-- Select Room --" : "No rooms available") : "Select a customer first"}
                        />

                        <FormField
                            label="Duration (minutes) *"
                            name="duration"
                            value={duration}
                            onChange={(_, val) => setDuration(val)}
                            placeholder="e.g. 15"
                        />

                        <div>
                            <FormField
                                label="Delay Duration (minutes)"
                                name="delay"
                                value={delay}
                                onChange={(_, val) => setDelay(val)}
                                placeholder="e.g. 5"
                            />
                            <p className="text-xs text-gray-500 mt-1">Delay before session starts (optional)</p>
                        </div>
                    </form>

                    {/* Footer */}
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3 rounded-b-none">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            className="border-gray-300 text-gray-700 hover:bg-white"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            onClick={handleSubmit}
                            className="bg-[#885ABB] hover:bg-[#6f47a2] text-white min-w-[100px]"
                        >
                            Start Session
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};
